import { NavLink, useLocation } from "react-router-dom";
import { GasPump, CashRegister, Checks, Wrench, ChartLine, Gear, SignOut } from "@phosphor-icons/react";
import StationPicker from "./StationPicker";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const tabs = [
  { to: "/", label: "Dashboard", icon: ChartLine, testId: "nav-tab-dashboard", end: true },
  { to: "/fuel", label: "Fuel", icon: GasPump, testId: "nav-tab-fuel" },
  { to: "/cash", label: "Cash", icon: CashRegister, testId: "nav-tab-cash" },
  { to: "/compliance", label: "UST", icon: Checks, testId: "nav-tab-compliance" },
  { to: "/maintenance", label: "Maint.", icon: Wrench, testId: "nav-tab-maintenance" },
];

export default function Shell({ children }) {
  const location = useLocation();
  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      <div className="mx-auto max-w-md min-h-screen relative">
        <Header />
        <main className="px-4 pt-4 pb-32" data-testid="app-main">
          {children}
        </main>
        <nav
          className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-40 border-t border-white/[0.06] bg-[#0F1115]/85 backdrop-blur-xl"
          data-testid="bottom-nav"
        >
          <ul className="grid grid-cols-5">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = t.end ? location.pathname === "/" : location.pathname.startsWith(t.to);
              return (
                <li key={t.to}>
                  <NavLink
                    to={t.to}
                    end={t.end}
                    data-testid={t.testId}
                    className={`relative flex flex-col items-center justify-center gap-1 h-16 tap-highlight-none transition-colors ${
                      active ? "text-[#FF6B00]" : "text-gray-500 hover:text-gray-300"
                    }`}
                  >
                    {active && (
                      <span className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-8 bg-[#FF6B00] rounded-full" />
                    )}
                    <Icon size={22} weight={active ? "fill" : "regular"} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider">{t.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}

function Header() {
  const { user, logout } = useAuth();
  const handleLogout = () => {
    logout();
    toast.success("Signed out");
  };
  return (
    <header
      className="sticky top-0 z-30 bg-[#0F1115]/85 backdrop-blur-xl border-b border-white/[0.06]"
      data-testid="app-header"
    >
      <div className="flex items-center justify-between px-4 h-14 gap-2">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="h-8 w-8 rounded-md bg-[#FF6B00] grid place-items-center">
            <GasPump size={18} weight="fill" className="text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black uppercase tracking-tight text-lg">FuelOps</div>
            {user && (
              <div className="text-[9px] uppercase tracking-[0.18em] text-gray-500 -mt-0.5 truncate max-w-[100px]">
                {user.email}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StationPicker />
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className="h-10 w-10 grid place-items-center rounded-lg border border-white/[0.06] text-gray-300 hover:text-white hover:border-white/20 transition-colors flex-shrink-0"
          >
            <Gear size={18} weight="bold" />
          </NavLink>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="logout-btn"
            aria-label="Sign out"
            className="h-10 w-10 grid place-items-center rounded-lg border border-white/[0.06] text-gray-300 hover:text-[#EF4444] hover:border-[#EF4444]/40 transition-colors flex-shrink-0"
          >
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </div>
    </header>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between mb-5 stagger-in gap-3">
      <div className="min-w-0">
        <h1 className="font-display font-black uppercase tracking-tight text-3xl leading-none">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-1.5">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}
